import React, { useState, useEffect, useRef } from 'react';
import {
  Container,
  Row,
  Col,
  Card,
  Badge,
  Button,
  Alert,
  Collapse,
  ProgressBar,
  Tabs,
  Tab,
} from 'react-bootstrap';
import { useParams, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  FaArrowLeft,
  FaPlay,
  FaPause,
  FaStop,
  FaCheck,
  FaEdit,
  FaShare,
  FaChevronDown,
  FaChevronUp,
  FaGithub,
  FaBlog,
  FaVideo,
  FaClock,
} from 'react-icons/fa';

import { selectAuthUserId } from '../redux/authSlice';
import { selectCourses } from '../redux/coursesSlice';
import { selectGeneratedCourse } from '../redux/courseDetailsSlice';
import LoadingSpinner from '../components/ui/LoadingSpinner';
import { ProjectGenerationService } from '../services/llmClient';
import {
  fetchProjectById,
  createMilestone,
  updateMilestone,
} from '../services/supabaseClient/projectService';
import { createBulkActivities } from '../services/supabaseClient/activitiesService';
import './ProjectDetailsPage.css';

const ProjectDetailsPage = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const userId = useSelector(selectAuthUserId);
  const courses = useSelector(selectCourses);
  const generatedCourse = useSelector(selectGeneratedCourse);

  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openMilestones, setOpenMilestones] = useState({});
  const [showAddMilestone, setShowAddMilestone] = useState(false);
  const [newMilestoneTitle, setNewMilestoneTitle] = useState('');
  const [newMilestoneDescription, setNewMilestoneDescription] = useState('');
  const [newMilestoneHours, setNewMilestoneHours] = useState(3);
  const [savingMilestone, setSavingMilestone] = useState(false);
  const [generatingTasksByMilestone, setGeneratingTasksByMilestone] = useState({});
  const [timerActive, setTimerActive] = useState(false);
  const [timerPaused, setTimerPaused] = useState(false);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const timerIntervalRef = useRef(null);

  useEffect(() => {
    if (userId && projectId) {
      loadProject();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, projectId]);

  // Timer effect
  useEffect(() => {
    if (timerActive && !timerPaused) {
      timerIntervalRef.current = setInterval(() => {
        setElapsedSeconds((prev) => prev + 1);
      }, 1000);
    } else {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
      }
    }

    return () => {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
      }
    };
  }, [timerActive, timerPaused]);

  // Update timer state when project loads
  useEffect(() => {
    if (project) {
      setTimerActive(project.timer_active || false);
      setTimerPaused(project.timer_paused || false);
      setElapsedSeconds(project.elapsed_seconds || 0);
    }
  }, [project]);

  const linkedCourse =
    (project?.course_id && courses.find((course) => course.id === project.course_id)) ||
    (generatedCourse?.id && generatedCourse.id === project?.course_id ? generatedCourse : null) ||
    null;

  const loadProject = async () => {
    setLoading(true);
    setError(null);

    try {
      const { data, error: fetchError } = await fetchProjectById(projectId);

      if (fetchError) {
        setError(fetchError.message || 'Failed to load project');
        setLoading(false);
        return;
      }

      setProject(data);
      setLoading(false);
    } catch (err) {
      setError(err.message || 'Failed to load project');
      setLoading(false);
    }
  };

  const handleGenerateTasksForMilestone = async (milestone) => {
    if (!project || !milestone) return;

    setError(null);
    setGeneratingTasksByMilestone((prev) => ({ ...prev, [milestone.id]: true }));

    try {
      const aiResponse = await ProjectGenerationService.generateTasks({
        milestoneId: milestone.id,
        milestoneTitle: milestone.title,
        milestoneDescription: milestone.description,
        projectContext:
          project.description || `Project: ${project.title}. Milestone: ${milestone.title}.`,
        taskCount: 5,
      });

      if (!aiResponse?.success) {
        setError(aiResponse?.error || 'Failed to generate tasks for milestone');
        return;
      }

      const tasks = aiResponse.tasks || [];

      if (tasks.length === 0) {
        return;
      }

      const activitiesPayload = tasks.map((task, index) => ({
        title: task.title || `Task ${index + 1}`,
        description: task.description || '',
        estimatedMinutes: task.estimated_minutes || null,
        priority: task.priority || 'medium',
        status: 'todo',
        aiGenerated: true,
        projectId: project.id,
        courseId: project.course_id || null,
        tags: task.tags || [],
      }));

      const { error: bulkError } = await createBulkActivities(activitiesPayload);

      if (bulkError) {
        setError(bulkError.message || 'Failed to save generated tasks');
        return;
      }

      // Also update the milestone.tasks array so the checklist matches the generated tasks
      const normalizedTasks = tasks.map((task, index) => ({
        id: task.id || `${milestone.id}-task-${index + 1}`,
        title: task.title || `Task ${index + 1}`,
        description: task.description || null,
        completed: false,
        order_index: task.order_index ?? index + 1,
      }));

      const { error: milestoneUpdateError } = await updateMilestone(milestone.id, {
        tasks: normalizedTasks,
      });

      if (milestoneUpdateError) {
        setError(milestoneUpdateError.message || 'Failed to update milestone tasks');
      } else {
        await loadProject();
      }
    } catch (err) {
      setError(err.message || 'Failed to generate tasks for milestone');
    } finally {
      setGeneratingTasksByMilestone((prev) => ({ ...prev, [milestone.id]: false }));
    }
  };

  const handleCreateMilestone = async (e) => {
    e.preventDefault();
    if (!project) return;

    const title = newMilestoneTitle.trim();
    if (!title) {
      setError('Milestone title is required');
      return;
    }

    setSavingMilestone(true);
    setError(null);

    try {
      const { error: milestoneError } = await createMilestone({
        project_id: project.id,
        title,
        description: newMilestoneDescription.trim() || null,
        order_index: (project.milestones?.length || 0) + 1,
        status: 'pending',
        estimated_hours: Number.isFinite(Number(newMilestoneHours)) ? Number(newMilestoneHours) : 3,
      });

      if (milestoneError) {
        setError(milestoneError.message || 'Failed to create milestone');
      } else {
        setNewMilestoneTitle('');
        setNewMilestoneDescription('');
        setNewMilestoneHours(3);
        setShowAddMilestone(false);
        await loadProject();
      }
    } catch (err) {
      setError(err.message || 'Failed to create milestone');
    } finally {
      setSavingMilestone(false);
    }
  };

  const toggleMilestone = (milestoneId) => {
    setOpenMilestones((prev) => ({
      ...prev,
      [milestoneId]: !prev[milestoneId],
    }));
  };

  const formatTime = (seconds) => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    if (hrs > 0) {
      return `${hrs}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatDate = (isoString) => {
    if (!isoString) return null;
    const date = new Date(isoString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  const handleStartTimer = () => {
    setTimerActive(true);
    setTimerPaused(false);
    // TODO: Call API to start timer
  };

  const handlePauseTimer = () => {
    setTimerPaused(true);
    // TODO: Call API to pause timer
  };

  const handleResumeTimer = () => {
    setTimerPaused(false);
    // TODO: Call API to resume timer
  };

  const handleStopTimer = () => {
    setTimerActive(false);
    setTimerPaused(false);
    // TODO: Call API to stop timer
  };

  const handleOpenPromptStudio = () => {
    if (!project) return;

    const params = new URLSearchParams({
      type: 'project',
      mode: 'implementation',
      projectId: project.id,
    });

    if (project.course_id || linkedCourse?.id) {
      params.set('courseId', project.course_id || linkedCourse.id);
    }

    navigate(`/prompts?${params.toString()}`);
  };

  const getStatusVariant = (status) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'in_progress':
        return 'primary';
      case 'pending':
        return 'secondary';
      default:
        return 'secondary';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'completed':
        return 'Completed';
      case 'in_progress':
        return 'In Progress';
      case 'pending':
        return 'Pending';
      case 'not_started':
        return 'Not Started';
      default:
        return status;
    }
  };

  const getSharingIcon = (platform) => {
    switch (platform) {
      case 'github':
        return <FaGithub />;
      case 'blog':
        return <FaBlog />;
      case 'video':
        return <FaVideo />;
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <Container className="project-details-page py-5">
        <LoadingSpinner />
      </Container>
    );
  }

  if (error || !project) {
    return (
      <Container className="project-details-page py-5">
        <Alert variant="danger">
          <h4>Error</h4>
          <p>{error || 'Project not found'}</p>
          <Button variant="outline-danger" onClick={() => navigate('/projects')}>
            <FaArrowLeft className="me-2" />
            Back to Projects
          </Button>
        </Alert>
      </Container>
    );
  }

  const completedMilestones =
    project.milestones?.filter((m) => m.status === 'completed').length || 0;
  const totalMilestones = project.milestones?.length || 0;

  return (
    <Container className="project-details-page py-4">
      {/* Back Button */}
      <Button
        variant="outline-secondary"
        onClick={() => navigate('/projects')}
        className="mb-4 back-button"
      >
        <FaArrowLeft className="me-2" />
        Back to Projects
      </Button>

      {/* Header Section */}
      <Card className="project-header-card mb-4">
        <Card.Body>
          <Row>
            <Col lg={8}>
              <div className="d-flex align-items-start gap-3 mb-3">
                <div className="project-icon">
                  <i className="fa-solid fa-diagram-project fa-3x" />
                </div>
                <div className="flex-grow-1">
                  <h1 className="project-detail-title">{project.title}</h1>
                  <div className="project-badges mb-2">
                    <Badge bg={getStatusVariant(project.status)} className="me-2">
                      {getStatusLabel(project.status)}
                    </Badge>
                    {project.difficulty_level && (
                      <Badge bg="info" className="me-2">
                        {project.difficulty_level}
                      </Badge>
                    )}
                    {project.ai_generated && (
                      <Badge bg="primary" className="me-2" aria-label="AI project">
                        <i className="fa-solid fa-robot" />
                      </Badge>
                    )}
                  </div>
                  <p className="text-muted mb-0">{project.description}</p>
                </div>
              </div>

              <div className="project-meta-info">
                {/* Timer Display */}
                {timerActive && (
                  <div className="meta-item timer-display-large">
                    <FaClock className="me-2" />
                    <strong>{formatTime(elapsedSeconds)}</strong>
                    {timerPaused && <span className="text-warning ms-2">(Paused)</span>}
                  </div>
                )}
                <div className="meta-item">
                  <i className="fa-solid fa-hourglass-half me-2" />
                  <span>Est: {project.estimated_hours}h</span>
                  {elapsedSeconds > 0 && (
                    <span className="ms-2">| Spent: {(elapsedSeconds / 3600).toFixed(1)}h</span>
                  )}
                </div>
                {project.technologies && project.technologies.length > 0 && (
                  <div className="meta-item">
                    <i className="fa-solid fa-code me-2" />
                    <span>{project.technologies.join(', ')}</span>
                  </div>
                )}
                {/* Date Information */}
                {project.created_at && (
                  <div className="meta-item">
                    <i className="fa-solid fa-calendar-plus me-2" />
                    <span>Created: {formatDate(project.created_at)}</span>
                  </div>
                )}
                {project.started_at && (
                  <div className="meta-item">
                    <i className="fa-solid fa-play-circle me-2" />
                    <span>Started: {formatDate(project.started_at)}</span>
                  </div>
                )}
                {project.last_worked_at && (
                  <div className="meta-item">
                    <i className="fa-solid fa-clock me-2" />
                    <span>Last worked: {formatDate(project.last_worked_at)}</span>
                  </div>
                )}
              </div>
            </Col>

            <Col lg={4}>
              <div className="project-progress-section">
                <h5>Overall Progress</h5>
                <div className="progress-stats mb-2">
                  <span className="progress-percentage">{project.progress_percentage}%</span>
                  <span className="text-muted">
                    {completedMilestones}/{totalMilestones} milestones
                  </span>
                </div>
                <ProgressBar
                  now={project.progress_percentage}
                  variant={project.status === 'completed' ? 'success' : 'info'}
                  className="mb-3"
                />

                <div className="action-buttons d-grid gap-2">
                  {/* Timer Controls */}
                  {!timerActive && project.status !== 'completed' && (
                    <Button variant="success" size="lg" onClick={handleStartTimer}>
                      <FaPlay className="me-2" />
                      Start
                    </Button>
                  )}
                  {timerActive && !timerPaused && (
                    <Button variant="warning" size="lg" onClick={handlePauseTimer}>
                      <FaPause className="me-2" />
                      Pause
                    </Button>
                  )}
                  {timerActive && timerPaused && (
                    <Button variant="success" size="lg" onClick={handleResumeTimer}>
                      <FaPlay className="me-2" />
                      Resume
                    </Button>
                  )}
                  {timerActive && (
                    <Button variant="danger" size="lg" onClick={handleStopTimer}>
                      <FaStop className="me-2" />
                      Stop
                    </Button>
                  )}
                  {project.status === 'in_progress' && (
                    <Button variant="success" size="lg">
                      <FaCheck className="me-2" />
                      Done
                    </Button>
                  )}
                  <Button variant="outline-secondary">
                    <FaEdit className="me-2" />
                    Edit
                  </Button>
                  <Button variant="outline-primary">
                    <FaShare className="me-2" />
                    Share
                  </Button>
                </div>
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Tabs Section */}
      <Tabs defaultActiveKey="milestones" className="mb-4 project-tabs">
        <Tab eventKey="milestones" title="Milestones">
          <div className="milestones-section text-start">
            <Card className="mb-3">
              <Card.Body>
                {showAddMilestone ? (
                  <form onSubmit={handleCreateMilestone}>
                    <Row className="g-2 align-items-end">
                      <Col md={4}>
                        <label className="form-label" htmlFor="new-milestone-title">
                          Title
                        </label>
                        <input
                          id="new-milestone-title"
                          type="text"
                          className="form-control"
                          value={newMilestoneTitle}
                          onChange={(e) => setNewMilestoneTitle(e.target.value)}
                          required
                        />
                      </Col>
                      <Col md={5}>
                        <label className="form-label" htmlFor="new-milestone-description">
                          Description
                        </label>
                        <input
                          id="new-milestone-description"
                          type="text"
                          className="form-control"
                          value={newMilestoneDescription}
                          onChange={(e) => setNewMilestoneDescription(e.target.value)}
                        />
                      </Col>
                      <Col md={2}>
                        <label className="form-label" htmlFor="new-milestone-hours">
                          Est. Hours
                        </label>
                        <input
                          id="new-milestone-hours"
                          type="number"
                          min="1"
                          className="form-control"
                          value={newMilestoneHours}
                          onChange={(e) => setNewMilestoneHours(e.target.value)}
                        />
                      </Col>
                      <Col md={1} className="d-flex gap-2 justify-content-end">
                        <Button
                          variant="secondary"
                          type="button"
                          disabled={savingMilestone}
                          onClick={() => {
                            setShowAddMilestone(false);
                            setNewMilestoneTitle('');
                            setNewMilestoneDescription('');
                            setNewMilestoneHours(3);
                          }}
                        >
                          Cancel
                        </Button>
                        <Button variant="primary" type="submit" disabled={savingMilestone}>
                          {savingMilestone ? 'Saving...' : 'Add'}
                        </Button>
                      </Col>
                    </Row>
                  </form>
                ) : (
                  <div className="d-flex justify-content-between align-items-center">
                    <h5 className="mb-0">Project milestones</h5>
                    <Button
                      variant="outline-primary"
                      size="sm"
                      onClick={() => setShowAddMilestone(true)}
                    >
                      + Add
                    </Button>
                  </div>
                )}
              </Card.Body>
            </Card>

            {project.milestones && project.milestones.length > 0 ? (
              project.milestones.map((milestone) => (
                <Card key={milestone.id} className="milestone-card mb-3">
                  <Card.Header
                    onClick={() => toggleMilestone(milestone.id)}
                    className="milestone-header"
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => e.key === 'Enter' && toggleMilestone(milestone.id)}
                  >
                    <div className="d-flex justify-content-between align-items-center">
                      <div className="d-flex align-items-center gap-3">
                        <Badge bg={getStatusVariant(milestone.status)}>
                          {getStatusLabel(milestone.status)}
                        </Badge>
                        <div>
                          <h5 className="mb-0">{milestone.title}</h5>
                          <small className="text-muted">{milestone.description}</small>
                        </div>
                      </div>
                      <div className="d-flex align-items-center gap-3">
                        <span className="text-muted">
                          <FaClock className="me-1" />
                          {milestone.estimated_hours}h
                        </span>
                        {openMilestones[milestone.id] ? <FaChevronUp /> : <FaChevronDown />}
                      </div>
                    </div>
                  </Card.Header>
                  <Collapse in={openMilestones[milestone.id]}>
                    <Card.Body>
                      {milestone.tasks && milestone.tasks.length > 0 && (
                        <div className="milestone-tasks mb-3">
                          <h6>Tasks</h6>
                          {milestone.tasks.map((task) => (
                            <div key={task.id} className="task-item">
                              <input
                                type="checkbox"
                                checked={task.completed}
                                onChange={() => {}}
                                className="me-2"
                              />
                              <span
                                className={task.completed ? 'text-decoration-line-through' : ''}
                              >
                                {task.title}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}

                      {milestone.resources && milestone.resources.length > 0 && (
                        <div className="milestone-resources">
                          <h6>Resources</h6>
                          <ul>
                            {milestone.resources.map((resource, index) => (
                              <li key={index}>
                                <a href={resource} target="_blank" rel="noopener noreferrer">
                                  {resource}
                                </a>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      <div className="milestone-actions mt-3">
                        <Button
                          variant="outline-primary"
                          size="sm"
                          className="me-2"
                          disabled={!!generatingTasksByMilestone[milestone.id]}
                          onClick={() => handleGenerateTasksForMilestone(milestone)}
                        >
                          <FaPlay className="me-2" />
                          Gen
                        </Button>
                        <Button variant="primary" size="sm">
                          <FaCheck className="me-2" />
                          Done
                        </Button>
                      </div>
                    </Card.Body>
                  </Collapse>
                </Card>
              ))
            ) : (
              <Alert variant="info">No milestones defined for this project yet.</Alert>
            )}
          </div>
        </Tab>

        <Tab eventKey="overview" title="Overview">
          <Card>
            <Card.Body>
              <Row>
                <Col md={6}>
                  <h5>Learning Objectives</h5>
                  {project.learning_objectives && project.learning_objectives.length > 0 ? (
                    <ul>
                      {project.learning_objectives.map((objective, index) => (
                        <li key={index}>{objective}</li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-muted">No learning objectives defined.</p>
                  )}

                  <h5 className="mt-4">Prerequisites</h5>
                  {project.prerequisites && project.prerequisites.length > 0 ? (
                    <ul>
                      {project.prerequisites.map((prereq, index) => (
                        <li key={index}>{prereq}</li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-muted">No prerequisites defined.</p>
                  )}
                </Col>

                <Col md={6}>
                  <h5>Deliverables</h5>
                  {project.deliverables && project.deliverables.length > 0 ? (
                    <ul>
                      {project.deliverables.map((deliverable, index) => (
                        <li key={index}>{deliverable}</li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-muted">No deliverables defined.</p>
                  )}

                  <h5 className="mt-4">Technologies</h5>
                  {project.technologies && project.technologies.length > 0 ? (
                    <div className="d-flex flex-wrap gap-2">
                      {project.technologies.map((tech, index) => (
                        <Badge key={index} bg="secondary">
                          {tech}
                        </Badge>
                      ))}
                    </div>
                  ) : (
                    <p className="text-muted">No technologies specified.</p>
                  )}
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Tab>

        <Tab eventKey="promptCompiler" title="Prompt Generator">
          <div className="project-prompt-tab">
            <Card className="mb-4">
              <Card.Body>
                <div className="project-prompt-header">
                  <div>
                    <h5 className="mb-1">Open Prompt Studio for this project</h5>
                    <p className="text-muted mb-0">
                      Prompt generation now lives in a dedicated workflow so all your prompts can be
                      created, saved, and revisited in one place.
                    </p>
                  </div>
                  <Badge bg="dark" className="project-prompt-course-badge">
                    {linkedCourse?.title || 'No linked course found'}
                  </Badge>
                </div>

                {!linkedCourse ? (
                  <Alert variant="warning" className="mt-3 mb-0">
                    This project is not linked to a course in local state yet. Prompt Studio will
                    still open with the project preselected, but course grounding may be limited.
                  </Alert>
                ) : null}

                <div className="project-prompt-actions">
                  <Button variant="primary" onClick={handleOpenPromptStudio}>
                    <FaPlay className="me-2" />
                    Open Prompt Studio
                  </Button>
                  <Button variant="outline-secondary" onClick={() => navigate('/prompts')}>
                    View all prompts
                  </Button>
                </div>
              </Card.Body>
            </Card>
          </div>
        </Tab>

        <Tab eventKey="sharing" title="Sharing">
          <Card>
            <Card.Body>
              <h5>Share Your Project</h5>
              <p className="text-muted mb-4">
                Share your project on various platforms to showcase your work.
              </p>

              {project.sharing_platforms && project.sharing_platforms.length > 0 ? (
                <div className="sharing-platforms">
                  {project.sharing_platforms.map((platform, index) => (
                    <div key={index} className="platform-item mb-3">
                      <div className="d-flex align-items-center gap-2 mb-2">
                        {getSharingIcon(platform)}
                        <strong className="text-capitalize">{platform}</strong>
                      </div>
                      <input
                        type="url"
                        className="form-control"
                        placeholder={`Enter ${platform} URL`}
                      />
                    </div>
                  ))}
                </div>
              ) : (
                <Alert variant="info">No sharing platforms configured for this project.</Alert>
              )}

              <div className="mt-4">
                <Button variant="primary">
                  <FaCheck className="me-2" />
                  Save
                </Button>
              </div>
            </Card.Body>
          </Card>
        </Tab>
      </Tabs>
    </Container>
  );
};

export default ProjectDetailsPage;
